import { useEffect, useMemo, useState } from "react";

import { hasAnnotationTargetContent } from "./annotationTargetContent";
import type { AnnotationFrameDraft, AnnotationTarget } from "./types";
import { useStableHandler } from "./useStableHandler";

/**
 * read-only 미리보기의 slot 표시 상태. 편집 viewport의 target visibility와 독립이며 annotation을
 * 바꾸지 않는다. Studio가 하나를 만들어 패널에 넘기므로 inline↔popup 재배치에도 선택이 남는다.
 */
export interface CapturedFramePreviewSelection {
  /** 현재 frame에서 annotation이 있는 slot. 선택·강조는 이 집합 안에서만 성립한다. */
  readonly annotatedTargetIds: ReadonlySet<string>;
  /** 사용자가 켜 둔 slot. `aria-pressed`가 따르는 값이다. */
  readonly selectedTargetIds: ReadonlySet<string>;
  /** 실제로 그리는 slot — 선택에 일시 강조 대상을 더한다. */
  readonly visibleTargetIds: ReadonlySet<string>;
  readonly emphasizedTargetIds: ReadonlySet<string>;
  toggleTarget(targetId: string): void;
  showAllTargets(): void;
  setHoveredTarget(targetId: string | null): void;
  setFocusedTarget(targetId: string | null): void;
  clearEmphasis(): void;
}

export interface CapturedFramePreviewSelectionOptions {
  /** 열린 미리보기의 identity. 바뀌거나 닫혔다 다시 열리면 전체 표시로 돌아간다. */
  readonly previewFrameId: string | null;
  /** 패널에 넘기는 것과 같은 materialized draft. 아직 없으면 빈 집합이다. */
  readonly frame: AnnotationFrameDraft | undefined;
  readonly targets: readonly AnnotationTarget[];
}

type EmphasisSource = "hover" | "focus";

type SelectionState = Readonly<{
  frameId: string | null;
  /** `all`은 이후 annotation이 생기는 slot도 포함한다. `custom`은 고른 slot만 남긴다. */
  mode: "all" | "custom";
  targetIds: readonly string[];
  hoveredTargetId: string | null;
  focusedTargetId: string | null;
  /** hover와 focus가 동시에 있으면 나중 의도가 강조를 가진다. */
  lastEmphasis: EmphasisSource | null;
}>;

const EMPTY_IDS: ReadonlySet<string> = new Set();

function createSelectionState(frameId: string | null): SelectionState {
  return {
    frameId,
    mode: "all",
    targetIds: [],
    hoveredTargetId: null,
    focusedTargetId: null,
    lastEmphasis: null,
  };
}

function annotatedTargetIdsOf(
  frame: AnnotationFrameDraft | undefined,
  targets: readonly AnnotationTarget[],
): ReadonlySet<string> {
  if (!frame) return EMPTY_IDS;
  return new Set(
    targets.flatMap((target) => {
      const draft = frame.targets[target.id];
      return draft && hasAnnotationTargetContent(draft) ? [target.id] : [];
    }),
  );
}

function selectedTargetIdsOf(
  state: SelectionState,
  annotatedTargetIds: ReadonlySet<string>,
): ReadonlySet<string> {
  if (state.mode === "all") return annotatedTargetIds;
  return new Set(state.targetIds.filter((targetId) => annotatedTargetIds.has(targetId)));
}

function withSelection(
  state: SelectionState,
  nextTargetIds: readonly string[],
  annotatedTargetIds: ReadonlySet<string>,
): SelectionState {
  const normalized = [...annotatedTargetIds].filter((targetId) => nextTargetIds.includes(targetId));
  const all = normalized.length === annotatedTargetIds.size;
  return {
    ...state,
    mode: all ? "all" : "custom",
    targetIds: all ? [] : normalized,
    hoveredTargetId: null,
    focusedTargetId: null,
    lastEmphasis: null,
  };
}

export function useCapturedFramePreviewSelection({
  previewFrameId,
  frame,
  targets,
}: CapturedFramePreviewSelectionOptions): CapturedFramePreviewSelection {
  const [state, setState] = useState<SelectionState>(() => createSelectionState(previewFrameId));
  // 저장된 상태가 다른 frame의 것이면 읽는 순간 새 상태로 본다 — 한 렌더도 이전 frame의 선택을 보이지 않는다.
  const resolve = (current: SelectionState) =>
    current.frameId === previewFrameId ? current : createSelectionState(previewFrameId);
  const current = resolve(state);
  useEffect(() => {
    // 닫힘(null)도 저장해 두어야 같은 frame을 다시 열 때 전체 표시로 시작한다.
    setState((stored) =>
      stored.frameId === previewFrameId ? stored : createSelectionState(previewFrameId),
    );
  }, [previewFrameId]);

  const annotatedTargetIds = useMemo(() => annotatedTargetIdsOf(frame, targets), [frame, targets]);
  const derived = useMemo(() => {
    const selectedTargetIds = selectedTargetIdsOf(current, annotatedTargetIds);
    const emphasisTargetId =
      current.lastEmphasis === "focus"
        ? (current.focusedTargetId ?? current.hoveredTargetId)
        : (current.hoveredTargetId ?? current.focusedTargetId);
    const emphasized =
      emphasisTargetId !== null && annotatedTargetIds.has(emphasisTargetId)
        ? emphasisTargetId
        : null;
    const visibleTargetIds = new Set(selectedTargetIds);
    if (emphasized !== null) visibleTargetIds.add(emphasized);
    return {
      selectedTargetIds,
      visibleTargetIds,
      emphasizedTargetIds: emphasized === null ? EMPTY_IDS : new Set([emphasized]),
    };
  }, [current, annotatedTargetIds]);

  const toggleTarget = useStableHandler((targetId: string) => {
    if (!annotatedTargetIds.has(targetId)) return;
    setState((stored) => {
      const resolved = resolve(stored);
      const selected = selectedTargetIdsOf(resolved, annotatedTargetIds);
      const next = selected.has(targetId)
        ? [...selected].filter((id) => id !== targetId)
        : [...selected, targetId];
      return withSelection(resolved, next, annotatedTargetIds);
    });
  });
  const showAllTargets = useStableHandler(() => {
    setState((stored) =>
      withSelection(resolve(stored), [...annotatedTargetIds], annotatedTargetIds),
    );
  });
  const setEmphasis = (source: EmphasisSource, targetId: string | null) => {
    // annotation 없는 slot은 강조 대상이 아니다 — 무시해야 기존 focus 강조가 밀려나지 않는다.
    if (targetId !== null && !annotatedTargetIds.has(targetId)) return;
    setState((stored) => {
      const resolved = resolve(stored);
      const key = source === "hover" ? "hoveredTargetId" : "focusedTargetId";
      const lastEmphasis = targetId === null ? resolved.lastEmphasis : source;
      if (resolved[key] === targetId && resolved.lastEmphasis === lastEmphasis) return resolved;
      return { ...resolved, [key]: targetId, lastEmphasis };
    });
  };
  const setHoveredTarget = useStableHandler((targetId: string | null) =>
    setEmphasis("hover", targetId),
  );
  const setFocusedTarget = useStableHandler((targetId: string | null) =>
    setEmphasis("focus", targetId),
  );
  const clearEmphasis = useStableHandler(() => {
    setState((stored) => {
      const resolved = resolve(stored);
      if (resolved.hoveredTargetId === null && resolved.focusedTargetId === null) return resolved;
      return { ...resolved, hoveredTargetId: null, focusedTargetId: null, lastEmphasis: null };
    });
  });

  return useMemo(
    () => ({
      annotatedTargetIds,
      ...derived,
      toggleTarget,
      showAllTargets,
      setHoveredTarget,
      setFocusedTarget,
      clearEmphasis,
    }),
    [
      annotatedTargetIds,
      derived,
      toggleTarget,
      showAllTargets,
      setHoveredTarget,
      setFocusedTarget,
      clearEmphasis,
    ],
  );
}
